/**
 * space_fx — explosions and impacts under VACUUM rules, batched (report §9 R5 + §6 R6).
 *
 * The sector minted one `SpriteMaterial` per spark, flash, fireball and smoke puff (and freed it later) and drew
 * the "shockwave" as a camera-facing ring sprite — a HUD circle — with LDR fireballs that never bloomed and a
 * smoke shroud that lingered as if there were air. Here:
 *  - ONE instanced billboard batch (capacity FX_CAP, a fixed draw) for every flash, fireball, ember, spark and
 *    shrapnel streak: three vec4s per instance — (centre, size), (HDR rgb, alpha), (stretch vector, mode) —
 *    round soft sprites for mode 0, velocity-STRETCHED streaks for mode 1. Positions are kept ABSOLUTE on the
 *    CPU and written offset-local at every commit, so a floating-origin rebase never shifts a live effect.
 *  - Flash: a 30+ HDR spike (space_look.flashIntensity) + a POOLED point light (2–4 lights created at init,
 *    intensity 0 idle — the light-set identity law) that lights the lit war hulls around the blast.
 *  - Fireball: cools white → orange → dark red (space_look.coolingColor), expands and dies — NO smoke.
 *  - Shock: a PLANAR ring in the hull's plane (an instanced annulus, capacity SHOCK_CAP) growing as
 *    r(t) = R(1 − e^{−t/τ}) with a noise-broken HDR rim, fading — never a camera-facing sprite.
 *  - Shrapnel: velocity-stretched hot streaks that cool and keep drifting (vacuum: no drag).
 * Every material is built once per session (`userData.shared`); the batch and ring meshes are session objects
 * re-parented under each sector root (the sector sweep never frees them).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, vec2, vec3, vec4, float, int, attribute, cameraProjectionMatrix, modelViewMatrix, positionGeometry, screenSize,
  uv, length, normalize, smoothstep, clamp, max, mix, select, exp, abs, atan, uniform, time, mx_noise_float,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { coolingColor, flashIntensity, shockRadius, fxSignificance, fxLiveSignificance, fxEvictIndex, FX_KIND_WEIGHT, SHOCK_RING } from './space_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** Slots kept for the live bolts above the effects' budget (a full batch of debris never hides the shots). */
export const BOLT_RESERVE = 128;
/** The batch's GPU capacity: the highest tier budget (space_look.fxBudget) + the bolt reserve. */
export const FX_CAP = 1536 + BOLT_RESERVE;
export const SHOCK_CAP = 12;

// dropped = spawns refused (less significant than everything live); evicted = live entries replaced by a more
// significant spawn; peak = the most effects alive at once; budget = this sector's live cap (space_look.fxBudget).
export const spaceFxState = { live: 0, dropped: 0, evicted: 0, peak: 0, budget: 0, shocks: 0, lights: 0, lightsLit: 0, spawned: 0, bolts: 0 };

type Kind = 'flash' | 'fireball' | 'ember' | 'spark' | 'shrapnel';
interface Fx {
  kind: Kind; x: number; y: number; z: number; vx: number; vy: number; vz: number;
  t: number; life: number; s0: number; s1: number; r: number; g: number; b: number; gain: number;
  /** significance at spawn (space_look.fxSignificance), set by push */
  sig?: number;
}
interface Shock { x: number; y: number; z: number; q: THREE.Quaternion; t: number; life: number; R: number; tau: number; r: number; g: number; b: number; seed: number }
interface LightSlot { light: THREE.PointLight; t: number; life: number; peak: number; x: number; y: number; z: number }

let batch: { mesh: THREE.Mesh; a0: THREE.InstancedBufferAttribute; a1: THREE.InstancedBufferAttribute; a2: THREE.InstancedBufferAttribute } | null = null;
let rings: THREE.InstancedMesh | null = null;
let ringData: THREE.InstancedBufferAttribute | null = null;

function buildBatch(): NonNullable<typeof batch> {
  if (batch) return batch;
  const quad = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setIndex(quad.getIndex());
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  const mk = (): THREE.InstancedBufferAttribute => { const a = new THREE.InstancedBufferAttribute(new Float32Array(FX_CAP * 4), 4); a.setUsage(THREE.DynamicDrawUsage); return a; };
  const a0 = mk(), a1 = mk(), a2 = mk();
  geo.setAttribute('aPos', a0); geo.setAttribute('aCol', a1); geo.setAttribute('aStr', a2);
  geo.instanceCount = 0; // tick() draws exactly the live instances
  geo.userData.shared = true;
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  const P = attribute('aPos', 'vec4'), C = attribute('aCol', 'vec4'), S = attribute('aStr', 'vec4');
  mat.vertexNode = Fn(() => {
    const head = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(P.xyz, 1.0)));
    const g = positionGeometry.xy;
    const streak = S.w.greaterThan(0.5);
    // mode 0: a camera-facing quad of world size P.w (offset in clip space, perspective-correct).
    const p00 = cameraProjectionMatrix.element(int(0)).x, p11 = cameraProjectionMatrix.element(int(1)).y;
    const sizeClip = vec2(P.w.mul(p00), P.w.mul(p11));
    const round = vec4(head.xy.add(g.mul(sizeClip)), head.zw);
    // mode 1: a streak from the head back along the stretch vector, P.w wide in world metres (≥ 1.5 px).
    const tail = cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(P.xyz.sub(S.xyz), 1.0)));
    const hN = head.xy.div(max(head.w, float(1e-3))), tN = tail.xy.div(max(tail.w, float(1e-3)));
    const t = g.y.add(0.5);
    const along = mix(hN, tN, t);
    const dir = normalize(tN.sub(hN).add(vec2(1e-5, 0)));
    const wNdc = max(P.w.mul(p11).div(max(head.w, float(1e-3))), float(3).div(screenSize.y));
    const across = vec2(dir.y.negate(), dir.x).mul(g.x.mul(2)).mul(wNdc);
    const w = mix(head.w, tail.w, t);
    const line = vec4(along.add(across).mul(w), mix(head.z, tail.z, t), w);
    return select(streak, line, round);
  })();
  const streak = S.w.greaterThan(0.5);
  const r = length(uv().sub(0.5)).mul(2);
  const soft = exp(r.mul(r).mul(-5.5)).add(exp(r.mul(-9)).mul(0.6)).mul(float(1).sub(smoothstep(0.85, 1.0, r)));
  const head = float(1).sub(uv().y);
  const across = float(1).sub(abs(uv().x.sub(0.5)).mul(2));
  const line = head.mul(head).mul(across);
  mat.colorNode = C.xyz;
  mat.opacityNode = clamp(C.w.mul(select(streak, line, soft)), 0, 1);
  mat.name = 'space-fx-batch';
  markMrtOverlay(mat);
  mat.userData.shared = true;
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.visible = false;
  mesh.name = 'space-fx';
  mesh.renderOrder = 8;
  batch = { mesh, a0, a1, a2 };
  return batch;
}

function buildRings(): THREE.InstancedMesh {
  if (rings) return rings;
  // WHY a 7 % band (session 2): a 28 % band, then a 14 % one with a rim that filled it and an inner glow, both read
  // as a grey fog band over the battle (the boom shots). A shock front is a thin HOT edge (space_look.SHOCK_RING).
  const geo = new THREE.RingGeometry(SHOCK_RING.inner, 1, 96, 1);   // the XY plane; the instance matrix lays it in the hull's plane
  geo.deleteAttribute('uv');
  const data = new THREE.InstancedBufferAttribute(new Float32Array(SHOCK_CAP * 4), 4); // (age01, seed, gain, _)
  data.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aShock', data);
  geo.userData.shared = true;
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const A = attribute('aShock', 'vec4');
  const uTint = uniform(new THREE.Color(1, 0.85, 0.6));
  mat.colorNode = Fn(() => {
    const p = positionGeometry.xy;                               // MEASURE the raw annulus
    const rr = length(p);                                        // SHOCK_RING.inner … 1
    const ang = atan(p.y, p.x);
    const age = A.x;
    // space_look.shockRimProfile's mirror: zero at the inner lip, a hot peak at the front, zero at the rim.
    const rise = smoothstep(SHOCK_RING.inner, SHOCK_RING.peak, rr);
    const rim = rise.mul(rise).mul(float(1).sub(smoothstep(SHOCK_RING.peak, 1.0, rr)));
    const n = mx_noise_float(vec3(ang.mul(7), rr.mul(9), A.y.add(time.mul(0.6))));
    const broken = clamp(float(0.25).add(n.mul(1.4)), 0, 1.8);   // a front torn into arcs, never a clean HUD circle
    const k = float(1).sub(age);
    const fade = k.mul(k).mul(k);                                // space_look.shockFade
    const hot = mix(vec3(1, 1, 1), vec3(uTint), smoothstep(0.0, 0.35, age));
    return hot.mul(rim.mul(broken).mul(fade).mul(A.z));
  })();
  mat.opacityNode = float(1);
  mat.name = 'space-shock';
  markMrtOverlay(mat);
  mat.userData.shared = true;
  const m = new THREE.InstancedMesh(geo, mat, SHOCK_CAP);
  m.frustumCulled = false;
  m.name = 'space-shocks';
  m.renderOrder = 9;
  m.count = 0;
  rings = m;
  ringData = data;
  return m;
}

// The cooling table: coolingColor sampled once (the per-frame path reads 64 entries, never recomputes).
const COOL = Array.from({ length: 64 }, (_, i) => coolingColor(i / 63));
const cool = (t: number): { r: number; g: number; b: number; gain: number } => COOL[Math.min(63, Math.max(0, Math.round(t * 63)))];

const _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _m = new THREE.Matrix4();

/** The sector's FX: one instance per activate owns the live lists + the light pool; the GPU objects are the session's. */
export class SpaceFx {
  private live: Fx[] = [];
  private shocks: Shock[] = [];
  private lights: LightSlot[] = [];
  readonly group = new THREE.Group();
  /** The live cap (space_look.fxBudget of the tier, never past the capacity less the bolt reserve). */
  private readonly budget: number;
  /** The viewer's ABSOLUTE position (the last tick's) — what a spawn's significance is measured from. */
  private vx = 0; private vy = 0; private vz = 0;

  /**
   * `lightCount` pooled point lights (created NOW — at activate — and never flipped: parked at intensity 0);
   * `budget` the live-effect cap for this tier (space_look.fxBudget).
   */
  constructor(lightCount: number, budget = 768) {
    this.budget = Math.max(64, Math.min(FX_CAP - BOLT_RESERVE, Math.floor(budget)));
    spaceFxState.budget = this.budget;
    this.group.name = 'space-fx-root';
    const b = buildBatch();
    this.group.add(b.mesh, buildRings());
    for (let i = 0; i < lightCount; i++) {
      const light = new THREE.PointLight(0xffc890, 0, 0, 2);
      light.position.set(0, -1e7, 0); // parked out of every receiver's reach
      light.name = `space-fx-light-${i}`;
      this.group.add(light);
      this.lights.push({ light, t: 1, life: 0, peak: 0, x: 0, y: -1e7, z: 0 });
    }
    spaceFxState.lights = lightCount;
  }

  get count(): number { return this.live.length; }

  /**
   * Admit a spawn. Under the budget it simply joins; a FULL batch evicts the least significant live entry (its
   * spawn significance fading with its life) if the newcomer outranks it, else the newcomer is dropped. WHY: the
   * old rule refused the NEWEST at the cap, so a near capital's core flash could lose to sparks 40 km away.
   */
  private push(f: Fx): void {
    const L = this.live;
    f.sig = fxSignificance(FX_KIND_WEIGHT[f.kind], Math.max(f.s0, f.s1), Math.hypot(f.x - this.vx, f.y - this.vy, f.z - this.vz));
    if (L.length < this.budget) {
      L.push(f);
      if (L.length > spaceFxState.peak) spaceFxState.peak = L.length;
    } else {
      const i = fxEvictIndex(L.length, (k) => fxLiveSignificance(L[k].sig ?? 0, L[k].t, L[k].life), f.sig);
      if (i < 0) { spaceFxState.dropped++; return; }
      L[i] = f;
      spaceFxState.evicted++;
    }
    spaceFxState.spawned++;
  }

  /** A projectile impact (ABSOLUTE position): a white flash, sparks, a hot ember — no smoke in vacuum. */
  impact(x: number, y: number, z: number, scale: number, tintHex: number): void {
    const c = new THREE.Color(tintHex);
    this.push({ kind: 'flash', x, y, z, vx: 0, vy: 0, vz: 0, t: 0, life: 0.12, s0: scale * 2.2, s1: scale * 3.2, r: 1, g: 0.95, b: 0.9, gain: 1 });
    const n = Math.min(10, 4 + Math.round(scale / 6));
    for (let i = 0; i < n; i++) {
      const v = _p.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize().multiplyScalar(scale * (8 + Math.random() * 14));
      this.push({ kind: 'spark', x, y, z, vx: v.x, vy: v.y, vz: v.z, t: 0, life: 0.35 + Math.random() * 0.25, s0: scale * 0.08, s1: scale * 0.03, r: c.r, g: c.g, b: c.b, gain: 5 });
    }
    this.push({ kind: 'ember', x, y, z, vx: 0, vy: 0, vz: 0, t: 0, life: 0.45, s0: scale * 0.9, s1: scale * 1.6, r: 1, g: 0.6, b: 0.3, gain: 1 });
  }

  /**
   * A hull's core detonation (ABSOLUTE centre, hull length, civ tint, the hull's UP axis for the shock plane):
   * a 30+ flash + a pooled light, fireballs that cool, the planar shock, shrapnel streaks.
   */
  detonate(x: number, y: number, z: number, lenM: number, tintHex: number, up: THREE.Vector3): void {
    this.push({ kind: 'flash', x, y, z, vx: 0, vy: 0, vz: 0, t: 0, life: 0.35, s0: lenM * 0.9, s1: lenM * 1.6, r: 1, g: 0.97, b: 0.92, gain: 1 });
    const nf = lenM > 1000 ? 7 : 5;
    for (let i = 0; i < nf; i++) {
      const o = _p.set(Math.random() - 0.5, (Math.random() - 0.5) * 0.4, Math.random() - 0.5).multiplyScalar(lenM * 0.35);
      const v = _s.copy(o).normalize().multiplyScalar(lenM * (0.04 + Math.random() * 0.05));
      this.push({ kind: 'fireball', x: x + o.x, y: y + o.y, z: z + o.z, vx: v.x, vy: v.y, vz: v.z, t: 0, life: 0.7 + Math.random() * 0.8, s0: lenM * (0.1 + Math.random() * 0.08), s1: lenM * (0.28 + Math.random() * 0.12), r: 1, g: 1, b: 1, gain: 1 });
    }
    const ns = Math.min(48, 14 + Math.round(lenM / 120));
    const c = new THREE.Color(tintHex);
    for (let i = 0; i < ns; i++) {
      const v = _p.set(Math.random() - 0.5, (Math.random() - 0.5) * 0.6, Math.random() - 0.5).normalize().multiplyScalar(lenM * (0.25 + Math.random() * 0.5));
      this.push({ kind: 'shrapnel', x, y, z, vx: v.x, vy: v.y, vz: v.z, t: 0, life: 1.2 + Math.random() * 1.6, s0: Math.max(0.6, lenM * 0.004), s1: Math.max(0.3, lenM * 0.002), r: c.r * 0.5 + 0.5, g: c.g * 0.5 + 0.4, b: c.b * 0.4 + 0.2, gain: 6 });
    }
    // The planar shock in the hull's plane (its normal = the hull's up).
    if (this.shocks.length >= SHOCK_CAP) this.shocks.shift();
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), up.clone().normalize());
    this.shocks.push({ x, y, z, q, t: 0, life: lenM > 1000 ? 2.4 : 1.2, R: lenM * (lenM > 1000 ? 2.6 : 3.2), tau: lenM > 1000 ? 0.55 : 0.28, r: c.r, g: c.g, b: c.b, seed: Math.random() * 40 });
    spaceFxState.shocks++;
    // The pooled light: the slot furthest through its life.
    if (this.lights.length) {
      let best = this.lights[0];
      for (const l of this.lights) if (l.t / Math.max(1e-3, l.life) > best.t / Math.max(1e-3, best.life)) best = l;
      best.t = 0; best.life = lenM > 1000 ? 1.6 : 0.6; best.x = x; best.y = y; best.z = z;
      // Irradiance ≈ 3 (the sun's) at two hull lengths → candela = E·d².
      best.peak = 3 * (lenM * 2) * (lenM * 2);
      best.light.distance = lenM * 12;
    }
  }

  /** A war flash (warp-in / warp-out / a broadside bloom) — a short HDR bloom, no light. */
  flash(x: number, y: number, z: number, size: number, tintHex: number): void {
    const c = new THREE.Color(tintHex);
    this.push({ kind: 'flash', x, y, z, vx: 0, vy: 0, vz: 0, t: 0, life: 0.55, s0: size, s1: size * 2.2, r: c.r, g: c.g, b: c.b, gain: 0.5 });
  }

  /**
   * A wounded hull VENTING (R8, under 40 % hull): a fast white-blue gas jet along `(vx,vy,vz)` (stretched sparks
   * that vanish in vacuum within half a second) and, half the time, a burning ember that drifts off the plating.
   */
  vent(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number): void {
    for (let i = 0; i < 3; i++) {
      const j = 0.8 + Math.random() * 0.5;
      this.push({ kind: 'spark', x, y, z, vx: vx * j + (Math.random() - 0.5) * size, vy: vy * j + (Math.random() - 0.5) * size, vz: vz * j + (Math.random() - 0.5) * size, t: 0, life: 0.3 + Math.random() * 0.25, s0: size * 0.06, s1: size * 0.02, r: 0.75, g: 0.88, b: 1, gain: 2.2 });
    }
    if (Math.random() < 0.5) this.push({ kind: 'ember', x, y, z, vx: vx * 0.15, vy: vy * 0.15, vz: vz * 0.15, t: 0, life: 0.9, s0: size * 0.35, s1: size * 0.8, r: 1, g: 0.55, b: 0.2, gain: 1.6 });
  }

  /** A puff that is really a hot trail knot (torpedo exhaust) — cools and fades, no smoke. */
  knot(x: number, y: number, z: number, size: number): void {
    this.push({ kind: 'ember', x, y, z, vx: 0, vy: 0, vz: 0, t: 0, life: 0.8, s0: size, s1: size * 2.4, r: 0.8, g: 0.9, b: 1, gain: 0.8 });
  }

  /**
   * Per frame: integrate, cool, and write the batch offset-local (`off` = the floating origin). `bolts` (optional):
   * the live projectiles as HDR velocity-stretched streaks (root-local position, velocity m/s, their glow colour).
   * `viewer` (optional): the viewer's ABSOLUTE position — the next spawns' significance is measured from it.
   */
  tick(dt: number, off: THREE.Vector3, bolts?: ReadonlyArray<{ sprite: THREE.Object3D; vel: THREE.Vector3 }>, viewer?: { x: number; y: number; z: number }): void {
    const b = batch;
    if (!b) return;
    if (viewer) { this.vx = viewer.x; this.vy = viewer.y; this.vz = viewer.z; }
    // The group is a root child the floating-origin rebase shifts; everything below is written absolute − off.
    this.group.position.set(0, 0, 0);
    const L = this.live;
    for (let i = L.length - 1; i >= 0; i--) {
      const f = L[i];
      f.t += dt;
      if (f.t >= f.life) { L[i] = L[L.length - 1]; L.pop(); continue; }
      f.x += f.vx * dt; f.y += f.vy * dt; f.z += f.vz * dt;
    }
    const p0 = b.a0.array as Float32Array, p1 = b.a1.array as Float32Array, p2 = b.a2.array as Float32Array;
    const n = L.length;
    for (let i = 0; i < n; i++) {
      const f = L[i], o = i * 4, u = f.t / f.life;
      p0[o] = f.x - off.x; p0[o + 1] = f.y - off.y; p0[o + 2] = f.z - off.z;
      p0[o + 3] = f.s0 + (f.s1 - f.s0) * (1 - (1 - u) * (1 - u));
      let r = f.r, g = f.g, bb = f.b, gain = f.gain, a = 1;
      // The flash's afterglow dies in ~0.3 s whatever its life (session 2: a linear 0.4·(1−u) tail held a war flash as a
      // grey disc over half its life — a vacuum flash is a spike, the fireballs carry the glow).
      if (f.kind === 'flash') { gain = f.gain * flashIntensity(f.t) / 36 * 30 + 0.4 * Math.exp(-f.t / 0.1); a = 1 - u * u; }
      else if (f.kind === 'fireball') { const c = cool(u); r = c.r; g = c.g; bb = c.b; gain = c.gain; a = 1 - smoothstepCpu(0.7, 1, u); }
      else if (f.kind === 'shrapnel') { const c = cool(0.15 + u * 0.8); r = r * c.r; g = g * c.g; bb = bb * c.b; gain = f.gain * (1 - u) + 0.3; a = 1 - u; }
      else if (f.kind === 'spark') { gain = f.gain * (1 - u); a = 1 - u; }
      else { const c = cool(0.3 + u * 0.6); r = r * c.r; g = g * c.g; bb = bb * c.b; gain = f.gain * (1 - u) * 3; a = 1 - u; }
      p1[o] = r * gain; p1[o + 1] = g * gain; p1[o + 2] = bb * gain; p1[o + 3] = a;
      const streak = f.kind === 'shrapnel' || f.kind === 'spark';
      const k = streak ? 1 / 30 : 0;                      // a 1/30 s shutter's worth of travel
      p2[o] = f.vx * k; p2[o + 1] = f.vy * k; p2[o + 2] = f.vz * k; p2[o + 3] = streak ? 1 : 0;
    }
    // The bolts: HDR streaks (core > 1 so they bloom), a 1/30 s shutter of travel long, capped at 60 m.
    let m = n;
    if (bolts) {
      for (const bl of bolts) {
        if (m >= FX_CAP) break;
        const s = bl.sprite as THREE.Sprite;
        if (!s.visible || !s.parent) continue;
        const o = m * 4, v = bl.vel;
        const len = Math.min(60, v.length() / 30), k = len / Math.max(1e-3, v.length());
        p0[o] = s.position.x; p0[o + 1] = s.position.y; p0[o + 2] = s.position.z; p0[o + 3] = 0.9;
        const c = (s.material as THREE.SpriteMaterial | undefined)?.color;
        p1[o] = (c?.r ?? 0.5) * 6; p1[o + 1] = (c?.g ?? 0.9) * 6; p1[o + 2] = (c?.b ?? 1) * 6; p1[o + 3] = 1;
        p2[o] = v.x * k; p2[o + 1] = v.y * k; p2[o + 2] = v.z * k; p2[o + 3] = 1;
        m++;
      }
    }
    // Draw exactly the live instances and upload only their range (the capacity grew ×2.2 — the old path drew and
    // re-sent all 768 slots every frame). Ranges are REPLACED, never accumulated: a frame the batch is not drawn
    // would otherwise grow the list; [0, m) covers every instance the next draw reads.
    (b.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = m;
    if (m > 0) {
      b.a0.clearUpdateRanges(); b.a0.addUpdateRange(0, m * 4); b.a0.needsUpdate = true;
      b.a1.clearUpdateRanges(); b.a1.addUpdateRange(0, m * 4); b.a1.needsUpdate = true;
      b.a2.clearUpdateRanges(); b.a2.addUpdateRange(0, m * 4); b.a2.needsUpdate = true;
    }
    b.mesh.visible = m > 0;
    spaceFxState.live = n;
    spaceFxState.bolts = m - n;
    // Shocks.
    const R = rings, D = ringData;
    if (R && D) {
      const S = this.shocks;
      for (let i = S.length - 1; i >= 0; i--) { S[i].t += dt; if (S[i].t >= S[i].life) S.splice(i, 1); }
      const d = D.array as Float32Array;
      for (let i = 0; i < S.length; i++) {
        const s = S[i];
        const rad = Math.max(1, shockRadius(s.t, s.R, s.tau));
        _p.set(s.x - off.x, s.y - off.y, s.z - off.z);
        R.setMatrixAt(i, _m.compose(_p, s.q, _s.set(rad, rad, rad)));
        d[i * 4] = s.t / s.life; d[i * 4 + 1] = s.seed; d[i * 4 + 2] = SHOCK_RING.gain; d[i * 4 + 3] = 0;
      }
      R.count = S.length;
      if (S.length) { R.instanceMatrix.needsUpdate = true; D.needsUpdate = true; }
      R.visible = S.length > 0;
    }
    // Lights: decay, park when done (intensity 0, far below — never a visibility flip).
    let lit = 0;
    for (const l of this.lights) {
      if (l.t < l.life) {
        l.t += dt;
        const u = Math.min(1, l.t / l.life);
        l.light.intensity = l.peak * Math.exp(-u * 4) * (1 - u);
        l.light.position.set(l.x - off.x, l.y - off.y, l.z - off.z);
        lit++;
      } else if (l.light.intensity !== 0) { l.light.intensity = 0; l.light.position.set(0, -1e7, 0); }
    }
    spaceFxState.lightsLit = lit;
  }

  /** Leave the sector: the live lists go; the GPU objects stay the session's (re-parented at the next activate). */
  clear(): void {
    this.live.length = 0; this.shocks.length = 0;
    if (batch) { (batch.mesh.geometry as THREE.InstancedBufferGeometry).instanceCount = 0; batch.mesh.visible = false; }
  }
}

function smoothstepCpu(e0: number, e1: number, x: number): number { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); }
